/**
 * ============================================================================
 * GOOGLE DRIVE — AUTHENTICATION (Stage 3a)
 * ============================================================================
 *
 * Scope of this file, deliberately narrow per Stage 3a: obtain an OAuth
 * access token via Google Identity Services (GIS), retrieve the signed-in
 * account's email, and compare it against the single authorized account
 * configured at build time. NOTHING in this file talks to the Drive API
 * itself (no files.list, no folder logic, no upload) — that begins in
 * Stage 3b onward, per the approved staged plan.
 *
 * Two distinct concepts, kept separate throughout (see the Stage 1/3
 * architecture assessments):
 *   - AUTHENTICATION: "who is currently signed into Google?" — answered
 *     by requestDriveAccessToken() + fetchSignedInEmail().
 *   - AUTHORIZATION: "is that the one account this app is allowed to use
 *     for backup?" — answered by verifyAuthorizedAccount(), a pure
 *     function with no network dependency, directly unit-testable.
 * Obtaining a valid access token is NEVER treated as authorization by
 * itself — every caller must separately check verifyAuthorizedAccount()
 * before doing anything Drive-related with the token.
 *
 * Scopes requested: drive.file + userinfo.email — drive.file is the
 * narrowest Drive scope that lets this app create/read/update files it
 * creates itself (a "non-sensitive" scope, so it does not require
 * Google's app-verification review); userinfo.email is the smallest
 * addition needed to identify which account is signed in, since
 * drive.file alone does not expose account identity. Both scopes are
 * requested together in one initTokenClient() call — no separate/
 * broader scope is ever requested.
 *
 * SECURITY: this file never logs the access token, the signed-in email,
 * or any backup content. Only non-identifying booleans/status strings
 * are surfaced to callers for display.
 */

const DRIVE_AUTH_SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email';

export interface DriveTokenResult {
  accessToken: string;
}

export type DriveAuthErrorReason =
  | 'not_configured' // VITE_GOOGLE_CLIENT_ID missing — nothing to initialize
  | 'gis_unavailable' // the GIS script tag hasn't loaded (blocked, offline, etc.)
  | 'popup_blocked'
  | 'popup_closed'
  | 'access_denied' // user declined consent, or other OAuth error response
  | 'unknown';

export class DriveAuthError extends Error {
  reason: DriveAuthErrorReason;
  constructor(reason: DriveAuthErrorReason, message: string) {
    super(message);
    this.reason = reason;
    this.name = 'DriveAuthError';
  }
}

// Minimal shape of the pieces of the GIS global this file actually uses —
// deliberately narrow rather than pulling in a full third-party type
// package, consistent with "zero new npm dependencies".
interface GisTokenResponse {
  access_token?: string;
  error?: string;
}
interface GisTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}
interface GisOauth2Namespace {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GisTokenResponse) => void;
    error_callback?: (error: { type: string; message?: string }) => void;
  }) => GisTokenClient;
}
declare global {
  interface Window {
    google?: { accounts?: { oauth2?: GisOauth2Namespace } };
  }
}

let cachedTokenClient: GisTokenClient | null = null;

function getConfiguredClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID;
}

/** The configured authorized account, or undefined if none is set (see RecordsPage's Backup & Restore section). */
export function getAuthorizedAccountEmail(): string | undefined {
  return import.meta.env.VITE_AUTHORIZED_GOOGLE_EMAIL;
}

/**
 * Requests a drive.file + email access token via GIS's token model,
 * prompting the Google account chooser / consent screen as needed.
 * Resolves with the access token, or rejects with a DriveAuthError whose
 * `reason` lets the caller show a specific, honest message rather than a
 * generic failure for every case (popup blocked vs. user declined vs.
 * GIS not loaded are meaningfully different situations for the user).
 *
 * The token client is created once and reused across calls (GIS's own
 * recommended pattern), not recreated on every button click.
 */
export function requestDriveAccessToken(): Promise<DriveTokenResult> {
  const clientId = getConfiguredClientId();
  if (!clientId) {
    return Promise.reject(
      new DriveAuthError('not_configured', 'Google Drive backup is not configured for this deployment.')
    );
  }

  if (!window.google?.accounts?.oauth2) {
    return Promise.reject(
      new DriveAuthError('gis_unavailable', 'Could not reach Google Sign-In. Check your connection and try again.')
    );
  }

  return new Promise((resolve, reject) => {
    if (!cachedTokenClient) {
      cachedTokenClient = window.google!.accounts!.oauth2!.initTokenClient({
        client_id: clientId,
        scope: DRIVE_AUTH_SCOPES,
        callback: (response) => {
          if (response.error || !response.access_token) {
            reject(new DriveAuthError('access_denied', 'Google sign-in was not completed.'));
            return;
          }
          resolve({ accessToken: response.access_token });
        },
        error_callback: (error) => {
          if (error.type === 'popup_failed_to_open') {
            reject(new DriveAuthError('popup_blocked', 'The Google sign-in window could not be opened. Please allow pop-ups and try again.'));
          } else if (error.type === 'popup_closed') {
            reject(new DriveAuthError('popup_closed', 'Google sign-in was closed before completing.'));
          } else {
            reject(new DriveAuthError('unknown', 'Something went wrong signing in with Google.'));
          }
        },
      });
    }
    cachedTokenClient.requestAccessToken();
  });
}

/**
 * Calls Google's userinfo endpoint with the given access token to
 * retrieve the signed-in account's email. Requires the `email` scope to
 * have been granted (see DRIVE_AUTH_SCOPES above) — drive.file alone
 * does not expose this.
 */
export async function fetchSignedInEmail(accessToken: string): Promise<string> {
  const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new DriveAuthError('unknown', 'Could not verify the signed-in Google account.');
  }
  const data: unknown = await response.json();
  const email = (data as { email?: unknown }).email;
  if (typeof email !== 'string' || !email) {
    throw new DriveAuthError('unknown', 'Google did not return an account email.');
  }
  return email;
}

/**
 * Pure comparison — no network access, directly unit-testable. Emails
 * are compared case-insensitively (Google account emails are
 * effectively case-insensitive) after trimming incidental whitespace.
 * Returns false (never throws) when no authorized account is configured
 * at all, since an unconfigured deployment should never treat any
 * account as authorized by default.
 */
export function verifyAuthorizedAccount(signedInEmail: string, authorizedEmail: string | undefined): boolean {
  if (!authorizedEmail) return false;
  return signedInEmail.trim().toLowerCase() === authorizedEmail.trim().toLowerCase();
}
